import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { ModulePrice, ClientModule, ModuleStatus, ModuleWithStatus } from '@/types/marketplace';
import { ClientLicense, LicensePlan } from '@/types/license';

interface ClientLicenseWithPlan extends ClientLicense {
  license_plan?: LicensePlan;
}

export const useMarketplace = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile, user } = useAuth();

  // Fetch client_id from users table
  const { data: userData } = useQuery({
    queryKey: ['user-client-id', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('users')
        .select('client_id')
        .eq('id', user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const clientId = userData?.client_id;

  // Fetch all available modules
  const {
    data: allModules = [],
    isLoading: isLoadingModules,
  } = useQuery({
    queryKey: ['module-prices'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_prices')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as ModulePrice[];
    },
  });

  // Fetch client's active modules
  const {
    data: myModules = [],
    isLoading: isLoadingMyModules,
  } = useQuery({
    queryKey: ['client-modules-marketplace', clientId],
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from('client_modules')
        .select('*')
        .eq('client_id', clientId)
        .eq('is_active', true);

      if (error) throw error;
      return data as ClientModule[];
    },
    enabled: !!clientId,
  });

  // Fetch client's license
  const {
    data: myLicense,
    isLoading: isLoadingLicense,
  } = useQuery({
    queryKey: ['client-license', clientId],
    queryFn: async () => {
      if (!clientId) return null;

      const { data, error } = await supabase
        .from('client_licenses')
        .select(`
          *,
          license_plan:license_plans(*)
        `)
        .eq('client_id', clientId)
        .maybeSingle();

      if (error) throw error;
      return data as ClientLicenseWithPlan | null;
    },
    enabled: !!clientId,
  });

  // Check if client has a module
  const hasModule = (code: string): boolean => {
    return myModules.some(m => m.module_code === code && m.is_active);
  };

  // Get module status
  const getModuleStatus = (code: string): ModuleStatus => {
    const clientModule = myModules.find(m => m.module_code === code);
    if (clientModule?.is_active) {
      if (clientModule.pending_deactivation_at) {
        return 'pending_cancellation';
      }
      return 'active';
    }
    return 'available';
  };

  // Combine modules with status
  const modulesWithStatus: ModuleWithStatus[] = allModules.map(module => ({
    ...module,
    status: getModuleStatus(module.code),
    clientModule: myModules.find(m => m.module_code === module.code),
  }));

  const invalidateModuleQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['client-modules-marketplace'] });
    queryClient.invalidateQueries({ queryKey: ['client-modules'] });
    queryClient.invalidateQueries({ queryKey: ['client-modules-admin'] });
  };

  // Request module subscription
  const requestModuleMutation = useMutation({
    mutationFn: async (moduleCode: string) => {
      if (!clientId) throw new Error('Cliente não identificado');
      const { data, error } = await supabase.functions.invoke('add-module-subscription', {
        body: { clientId, moduleCode },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateModuleQueries();
      toast({
        title: 'Módulo ativado',
        description: 'O módulo foi adicionado à sua conta com sucesso.',
      });
    },
    onError: (error: any) => {
      console.error('Error requesting module:', error);
      toast({
        title: 'Erro ao solicitar módulo',
        description: error.message || 'Não foi possível adicionar o módulo.',
        variant: 'destructive',
      });
    },
  });

  // Schedule module deactivation
  const removeModuleMutation = useMutation({
    mutationFn: async (moduleCode: string) => {
      if (!clientId) throw new Error('Cliente não identificado');
      const { data, error } = await supabase.functions.invoke('remove-module-subscription', {
        body: { clientId, moduleCode },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateModuleQueries();
      toast({
        title: 'Desativação agendada',
        description: 'O módulo será desativado ao final do período de faturamento atual.',
      });
    },
    onError: (error: any) => {
      console.error('Error removing module:', error);
      toast({
        title: 'Erro ao desativar módulo',
        description: error.message || 'Não foi possível agendar a desativação do módulo.',
        variant: 'destructive',
      });
    },
  });

  // Reactivate module (cancel pending deactivation)
  const reactivateModuleMutation = useMutation({
    mutationFn: async (moduleCode: string) => {
      if (!clientId) throw new Error('Cliente não identificado');
      const { data, error } = await supabase.functions.invoke('reactivate-module', {
        body: { clientId, moduleCode },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      invalidateModuleQueries();
      toast({
        title: 'Módulo reativado',
        description: 'O cancelamento agendado foi removido. O módulo continuará ativo.',
      });
    },
    onError: (error: any) => {
      console.error('Error reactivating module:', error);
      toast({
        title: 'Erro ao reativar módulo',
        description: error.message || 'Não foi possível reativar o módulo.',
        variant: 'destructive',
      });
    },
  });

  // Metrics
  const activeModulesCount = myModules.filter(m => m.is_active).length;
  const availableModulesCount = allModules.length - activeModulesCount;
  const currentCost = myModules.reduce((total, clientModule) => {
    const modulePrice = allModules.find(m => m.code === clientModule.module_code);
    return total + (modulePrice?.price || 0);
  }, 0);

  return {
    // Data
    allModules,
    myModules,
    myLicense,
    modulesWithStatus,

    // States
    isLoading: isLoadingModules || isLoadingMyModules || isLoadingLicense,
    isRequesting: requestModuleMutation.isPending,
    isRemoving: removeModuleMutation.isPending,
    isReactivating: reactivateModuleMutation.isPending,

    // Checks
    hasModule,
    getModuleStatus,

    // Actions
    requestModule: requestModuleMutation.mutateAsync,
    removeModule: removeModuleMutation.mutateAsync,
    reactivateModule: reactivateModuleMutation.mutateAsync,

    // Metrics
    activeModulesCount,
    availableModulesCount,
    currentCost,
    clientId,
  };
};
